import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import { Panel, Btn, PageHead } from "@/components/kit";
import { Lock, Globe, Eye, ExternalLink, RefreshCw, Loader2, AlertTriangle, MessageSquare, Images, Trophy, ShieldCheck, ShieldAlert } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const QCBadge = ({ p }) => {
    const q = p.qc;
    if (!q || q.skipped) return null;
    if (q.passed)
        return (
            <span
                className="mt-1 inline-flex items-center gap-1 border border-status-green/60 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-status-green"
                data-testid={`pub-qc-${p.id}`}
                title={q.lang_checked ? "Idioma del audio verificado con IA" : "Verificaciones técnicas superadas"}
            >
                <ShieldCheck size={11} /> QC aprobado{q.lang_checked ? " · idioma ✓" : ""}
            </span>
        );
    return (
        <span
            className="mt-1 inline-flex items-center gap-1 border border-status-red/60 px-1.5 py-0.5 text-[10px] uppercase tracking-wider"
            style={{ color: "#FF003C" }}
            data-testid={`pub-qc-${p.id}`}
            title={(q.reasons || []).join(" · ")}
        >
            <ShieldAlert size={11} /> QC: {(q.reasons || ["revisar"])[0]?.slice(0, 40)}
        </span>
    );
};

const ABSummary = ({ p }) => {
    const ab = p.ab_test;
    if (!ab) return null;
    return (
        <div className="mt-2 border-l-2 border-primary/60 bg-muted/20 p-2 text-[11px]" data-testid={`ab-summary-${p.id}`}>
            <div className="mb-1 flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider text-primary">
                <Images size={11} /> A/B miniaturas ·{" "}
                {ab.status === "running"
                    ? `probando ${(ab.active ?? 0) + 1}/${ab.variants?.length}`
                    : "completada"}
            </div>
            <div className="flex flex-wrap items-center gap-2">
                {(ab.variants || []).map((v, i) => (
                    <div key={i} className="relative">
                        <img
                            src={`${API_BASE}${v.url}`}
                            alt={v.hook}
                            className={`h-12 w-20 border object-cover ${
                                ab.status === "done" && ab.winner === v.idx
                                    ? "border-status-green"
                                    : ab.active === v.idx && ab.status === "running"
                                    ? "border-primary"
                                    : "border-border opacity-70"
                            }`}
                        />
                        {ab.status === "done" && ab.winner === v.idx && (
                            <Trophy size={12} className="absolute -right-1 -top-1 text-status-green" />
                        )}
                        {v.per_hour != null && (
                            <span className="absolute bottom-0 left-0 bg-black/70 px-1 text-[8px] text-white">
                                {v.per_hour}/h
                            </span>
                        )}
                    </div>
                ))}
            </div>
            {ab.status === "done" && (
                <div className="mt-1 text-status-green">
                    Ganadora: variante {(ab.winner ?? 0) + 1} · aplicada permanentemente
                </div>
            )}
        </div>
    );
};

const visIcon = { private: Lock, unlisted: Eye, public: Globe };

const StatusCell = ({ p }) => {
    if (p.status === "uploading")
        return (
            <span className="flex items-center gap-1.5 text-xs uppercase text-primary" data-testid={`pub-status-${p.id}`}>
                <Loader2 size={12} className="animate-spin" /> Subiendo a YouTube…
            </span>
        );
    if (p.status === "queued" || (p.status === "failed" && p.quota_blocked))
        return (
            <span className="flex items-center gap-1.5 text-xs uppercase" style={{ color: "#F5A623" }} data-testid={`pub-status-${p.id}`} title={p.upload_error}>
                <RefreshCw size={12} /> En cola · reintento auto
            </span>
        );
    if (p.status === "failed")
        return (
            <span className="flex items-center gap-1.5 text-xs uppercase" style={{ color: "#FF003C" }} data-testid={`pub-status-${p.id}`} title={p.upload_error}>
                <AlertTriangle size={12} /> Falló
            </span>
        );
    if (p.youtube_url)
        return (
            <a
                href={p.youtube_url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-1.5 text-xs uppercase text-status-green underline"
                data-testid={`pub-youtube-link-${p.id}`}
            >
                <ExternalLink size={12} /> Ver en YouTube
            </a>
        );
    return (
        <span className="text-xs uppercase text-muted-foreground" data-testid={`pub-status-${p.id}`}>
            Simulado
        </span>
    );
};

export default function Publishing() {
    const qc = useQueryClient();
    const { data: pubs = [] } = useQuery({
        queryKey: ["publications"],
        queryFn: async () => (await api.get("/publications")).data,
        refetchInterval: (q) =>
            (q.state.data || []).some((p) => p.status === "uploading") ? 4000 : false,
    });

    const setVis = useMutation({
        mutationFn: async ({ id, visibility }) =>
            (await api.post(`/publications/${id}/visibility?visibility=${visibility}`)).data,
        onSuccess: (_, v) => {
            toast.success(`yutu · visibilidad → ${v.visibility}`);
            qc.invalidateQueries({ queryKey: ["publications"] });
        },
        onError: (e) => toast.error(e?.response?.data?.detail || "Fallo cambiando visibilidad"),
    });

    const retry = useMutation({
        mutationFn: async (id) => (await api.post(`/publications/${id}/retry`)).data,
        onSuccess: () => {
            toast.success("Subida REAL a YouTube iniciada");
            qc.invalidateQueries({ queryKey: ["publications"] });
        },
        onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo iniciar la subida"),
    });

    const startAB = useMutation({
        mutationFn: async (id) => (await api.post(`/publications/${id}/ab-thumbnails`, {})).data,
        onSuccess: () => {
            toast.success("Prueba A/B de miniaturas iniciada — rotará y elegirá la ganadora sola");
            qc.invalidateQueries({ queryKey: ["publications"] });
        },
        onError: (e) => toast.error(e?.response?.data?.detail || "No se pudo iniciar la prueba A/B"),
    });

    return (
        <div data-testid="publishing-page">
            <PageHead
                title="PUBLICACIÓN Y COMUNIDAD · YUTU"
                sub="Subida REAL a YouTube con OAuth: SEO + disclosure de IA (containsSyntheticMedia). Sube private por defecto; hazlo público con 1 clic."
            />

            <Panel title="Videos publicados" testId="publications-table">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="label border-b border-border">
                                <th className="py-2 pr-4">Título</th>
                                <th className="py-2 pr-4">Canal</th>
                                <th className="py-2 pr-4">Fmt</th>
                                <th className="py-2 pr-4">IA</th>
                                <th className="py-2 pr-4">YouTube</th>
                                <th className="py-2 pr-4">Visibilidad</th>
                                <th className="py-2">Acción</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pubs.map((p) => {
                                const Icon = visIcon[p.visibility] || Lock;
                                return (
                                    <tr
                                        key={p.id}
                                        className="border-b border-border/50 transition-colors hover:bg-accent"
                                        data-testid={`pub-row-${p.id}`}
                                    >
                                        <td className="py-3 pr-4">
                                            <div className="flex items-center gap-2">
                                                {p.dub_lang && (
                                                    <span
                                                        className="border border-primary px-1 py-0.5 font-mono text-[9px] uppercase text-primary"
                                                        data-testid={`pub-dub-${p.id}`}
                                                    >
                                                        🌐 {p.dub_lang}
                                                    </span>
                                                )}
                                                <span>{p.seo?.title || p.title}</span>
                                            </div>
                                            {p.dubs && Object.keys(p.dubs).length > 0 && (
                                                <div className="mt-1 text-[10px] text-status-green" data-testid={`pub-dubs-${p.id}`}>
                                                    Versiones automáticas: {Object.keys(p.dubs).map((l) => l.toUpperCase()).join(" · ")}
                                                </div>
                                            )}
                                            {p.dub_status === "pending" && !p.dub_lang && (
                                                <div className="mt-1 text-[10px] text-status-yellow">
                                                    Generando versiones EN/PT…
                                                </div>
                                            )}
                                            {p.engagement_comment && (
                                                <div
                                                    className="mt-1 flex items-start gap-1.5 text-[11px] text-muted-foreground"
                                                    data-testid={`pub-comment-${p.id}`}
                                                >
                                                    <MessageSquare size={12} className="mt-0.5 shrink-0 text-primary" />
                                                    <span>
                                                        Comentario publicado: “{p.engagement_comment}”{" "}
                                                        <span className="text-status-yellow">· fíjalo en YouTube Studio (1 clic)</span>
                                                    </span>
                                                </div>
                                            )}
                                            <ABSummary p={p} />
                                            <div><QCBadge p={p} /></div>
                                        </td>
                                        <td className="py-3 pr-4 text-xs text-primary">{p.channel_name}</td>
                                        <td className="py-3 pr-4 text-xs uppercase">{p.format}</td>
                                        <td className="py-3 pr-4 text-xs text-status-green">
                                            {p.ai_disclosure ? "✓ disclosure" : "—"}
                                        </td>
                                        <td className="py-3 pr-4">
                                            <StatusCell p={p} />
                                        </td>
                                        <td className="py-3 pr-4">
                                            <span className="flex items-center gap-1.5 text-xs uppercase">
                                                <Icon size={12} className="text-muted-foreground" /> {p.visibility}
                                            </span>
                                        </td>
                                        <td className="py-3">
                                            <div className="flex flex-wrap gap-2">
                                                {p.status === "failed" || (!p.youtube_video_id && p.status !== "uploading" && p.video_url) ? (
                                                    <Btn
                                                        testId={`pub-retry-${p.id}`}
                                                        variant="ghost"
                                                        className="px-2 py-1"
                                                        onClick={() => retry.mutate(p.id)}
                                                        disabled={retry.isPending}
                                                    >
                                                        <span className="flex items-center gap-1.5">
                                                            <RefreshCw size={12} /> Subir a YouTube
                                                        </span>
                                                    </Btn>
                                                ) : null}
                                                {p.status !== "uploading" &&
                                                    (p.visibility === "private" ? (
                                                        <Btn
                                                            testId={`publish-public-${p.id}`}
                                                            variant="green"
                                                            className="px-2 py-1"
                                                            onClick={() => setVis.mutate({ id: p.id, visibility: "public" })}
                                                        >
                                                            Publicar público
                                                        </Btn>
                                                    ) : (
                                                        <Btn
                                                            testId={`publish-private-${p.id}`}
                                                            variant="ghost"
                                                            className="px-2 py-1"
                                                            onClick={() => setVis.mutate({ id: p.id, visibility: "private" })}
                                                        >
                                                            Volver a private
                                                        </Btn>
                                                    ))}
                                                {p.youtube_video_id && p.format !== "short" && !p.ab_test && (
                                                    <Btn
                                                        testId={`ab-start-${p.id}`}
                                                        variant="ghost"
                                                        className="px-2 py-1"
                                                        onClick={() => startAB.mutate(p.id)}
                                                        disabled={startAB.isPending}
                                                    >
                                                        <span className="flex items-center gap-1.5">
                                                            <Images size={12} /> A/B miniaturas
                                                        </span>
                                                    </Btn>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {!pubs.length && (
                        <p className="py-6 text-sm text-muted-foreground">
                            Aún no hay publicaciones. Aprueba un video en la cola de aprobación.
                        </p>
                    )}
                </div>
            </Panel>
        </div>
    );
}
